"use client";

import { useSyncExternalStore } from "react";

import { compareVersions, SITE_VERSION } from "../../../lib/version";
import { markAppReady } from "../launch/ready";

// Storefront updates (F173, docs/storefront-updates.md). Open pages ask
// /version.json what's current and compare it with the version baked into
// their own code:
//   - same or older on the server (a rollback): nothing
//   - newer, own version ≥ its minVersion: "available" (the card on /app)
//   - own version < minVersion: "required" (the full screen)
// «بروزرسانی» reloads the same URL: pages always come from the network first,
// so the reload is the new build, which then registers its own worker
// (ServiceWorkerRegistration). If another tab already did and that worker is
// waiting, it takes over first.

export type UpdateState = { kind: "none" } | { kind: "available" | "required"; version: string };

const NONE: UpdateState = { kind: "none" };
let state: UpdateState = NONE;
let registration: ServiceWorkerRegistration | null = null;
let lastCheck = 0;
const listeners = new Set<() => void>();

function set(next: UpdateState) {
  state = next;
  listeners.forEach((l) => l());
}

/** ServiceWorkerRegistration hands over the site-wide worker, used by applyUpdate. */
export function setRegistration(reg: ServiceWorkerRegistration) {
  registration = reg;
}

/**
 * Asks /version.json, at most once per `minIntervalMs`. Fails open: any error
 * leaves the state as it was (offline is the offline page's job).
 */
export async function checkForUpdate(minIntervalMs = 0): Promise<void> {
  const now = Date.now();
  if (now - lastCheck < minIntervalMs) return;
  lastCheck = now;
  try {
    const res = await fetch("/version.json", { cache: "no-store", signal: AbortSignal.timeout(10_000) });
    if (!res.ok) return;
    const { version, minVersion } = (await res.json()) as { version?: string; minVersion?: string };
    if (typeof version !== "string" || typeof minVersion !== "string") return;
    if (compareVersions(SITE_VERSION, minVersion) < 0) {
      set({ kind: "required", version });
      // Nothing behind it can be used: the launch screen needn't wait for the page (F179).
      markAppReady();
    }
    else if (compareVersions(SITE_VERSION, version) < 0) set({ kind: "available", version });
    else set(NONE);
  } catch {
    /* offline, timeout: try again on the next trigger */
  }
}

/** The current update state; "none" during server rendering. */
export function useUpdateState(): UpdateState {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => state,
    () => NONE,
  );
}

function waitFor<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([promise, new Promise<undefined>((resolve) => setTimeout(resolve, ms))]);
}

/**
 * «بروزرسانی»: a waiting worker (if another tab already installed the new
 * build's) takes over, then one reload on the same URL, which gets the new
 * build from the network.
 */
export async function applyUpdate(): Promise<void> {
  const reg = registration;
  if (reg && "serviceWorker" in navigator) {
    // Still downloading: give it a moment to finish installing.
    const installing = reg.installing;
    if (!reg.waiting && installing) {
      await waitFor(
        new Promise<void>((resolve) => installing.addEventListener("statechange", () => installing.state !== "installing" && resolve())),
        8_000,
      );
    }
    const waiting = reg.waiting;
    if (waiting) {
      const switched = new Promise<void>((resolve) => navigator.serviceWorker.addEventListener("controllerchange", () => resolve(), { once: true }));
      waiting.postMessage({ type: "SKIP_WAITING" });
      await waitFor(switched, 4_000);
    }
  }
  window.location.reload();
}
